
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Building2, ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from '@/components/ui/sonner'
import { useAuth } from '@/hooks/useAuth'

const ForgotPasswordForm = () => {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [emailSent, setEmailSent] = useState(false)
  
  const { resetPassword } = useAuth()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!email) {
      toast.error('Digite seu email')
      return
    }

    setLoading(true)

    const { error } = await resetPassword(email)

    if (error) {
      toast.error(error)
    } else {
      setEmailSent(true)
      toast.success('Email de recuperação enviado!')
    }

    setLoading(false)
  }

  if (emailSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-6">
          <div className="text-center space-y-2">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                <Building2 className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">itaCondo</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">Gestão Residencial</p>
              </div>
            </div>
          </div>

          <Card>
            <CardHeader className="pb-4 text-center">
              <CardTitle className="text-lg">Email enviado!</CardTitle>
              <CardDescription>
                Verifique sua caixa de entrada e siga as instruções para redefinir sua senha.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="text-center text-sm text-gray-600 dark:text-gray-400">
                  Não recebeu o email? Verifique sua pasta de spam ou tente novamente.
                </div>
                <Button 
                  onClick={() => setEmailSent(false)}
                  variant="outline" 
                  className="w-full"
                >
                  Tentar novamente
                </Button>
                <Button asChild className="w-full">
                  <Link to="/login">
                    Voltar ao login
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
              <Building2 className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">itaCondo</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">Gestão Residencial</p>
            </div>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Recuperar senha</h2>
          <p className="text-gray-600 dark:text-gray-400">Digite seu email para receber as instruções</p>
        </div>

        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">Esqueceu sua senha?</CardTitle>
            <CardDescription>
              Enviaremos um link para redefinir sua senha
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={loading}
              >
                {loading ? 'Enviando...' : 'Enviar email de recuperação'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="text-center">
          <Button variant="ghost" asChild className="text-blue-600 hover:text-blue-700">
            <Link to="/login" className="flex items-center justify-center space-x-2">
              <ArrowLeft className="h-4 w-4" />
              <span>Voltar ao login</span>
            </Link>
          </Button>
        </div>
      </div>
    </div>
  )
}

export default ForgotPasswordForm
